import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Badge,
  Input,
  Separator,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
} from '@cargomatic/ui';
import { PlusCircle, Search } from 'lucide-react';
import { cn } from 'utils/ui/classes';
import LocationWebAPIUtils from 'utils/LocationWebAPIUtils';

export function DataTableTextFilter({ column, title, className }) {
  const [open, setOpen] = useState(false);
  const filterValues = column?.getFilterValue() ?? [];
  const selectedValues = useMemo(() => new Set(filterValues), [filterValues]);
  const [searchText, setSearchText] = useState('');
  const [customZones, setCustomZones] = useState([]);
  const inputRef = useRef();

  const customZoneNames = useMemo(() => {
    return (customZones ?? []).map((zone) => zone.name);
  }, [customZones]);

  async function fetchCustomZones() {
    const response = await LocationWebAPIUtils.getCustomZones();
    setCustomZones(response.zones);
  }
  useEffect(() => {
    fetchCustomZones();
  }, []);

  const selectedValuesCount = useMemo(
    () => filterValues.length,
    [filterValues],
  );

  const remainingValues = useMemo(() => {
    return filterValues.filter((val) => !customZoneNames.includes(val));
  }, [filterValues, customZoneNames]);

  function handleOpenChange(open) {
    if (open) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }
  function handleSearchTextChange(e) {
    const {
      target: { value },
    } = e;
    setSearchText(value);
  }

  function handleSearch() {
    column?.setFilterValue([...filterValues, searchText]);
    setSearchText('');
    hide();
  }

  function handleToggleZoneSelect(zoneName, checked) {
    if (!checked) {
      handleRemove(zoneName);
    } else {
      column?.setFilterValue([...filterValues, zoneName]);
      setSearchText('');
    }
    hide();
  }

  function handleRemove(value) {
    column?.setFilterValue(filterValues.filter((text) => text !== value));
    hide();
  }

  function handleReset() {
    setSearchText('');
    column.setFilterValue(undefined);
    hide();
  }

  function handleSearchKeyDown(e) {
    if (e.key === 'Enter') {
      handleSearch();
    }

    e.stopPropagation();
  }

  function hide() {
    setOpen(false);
  }

  return (
    <DropdownMenu modal={false} onOpenChange={handleOpenChange} open={open}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="border-dashed flex items-center"
          onClick={() => setOpen(true)}
        >
          <PlusCircle className="mr-2 h-4 w-4" />
          {title}
          {selectedValuesCount > 0 && (
            <>
              <Separator orientation="vertical" className="mx-2 h-4" />
              <div className="flex items-center space-x-2">
                {filterValues.map((value) => (
                  <Badge className="rounded-sm px-1 font-normal h-4 flex justify-between items-center">
                    <span>{value}</span>
                  </Badge>
                ))}
              </div>
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        onCloseAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={hide}
        onFocusOutside={hide}
        onEscapeKeyDown={hide}
        onPointerDownOutside={hide}
        className={cn('p-0 overflow-y-auto', className)}
        align="start"
        forceMount
      >
        <div className="flex w-full max-w-sm items-center space-x-1 px-2 py-1.5"></div>
        <div className="flex w-full max-w-sm items-center space-x-1 px-2 py-1.5">
          <Input
            type="search"
            placeholder={`Search ${title}`}
            onChange={handleSearchTextChange}
            onKeyDown={handleSearchKeyDown}
            className="flex-grow-0 flex-shrink-0j"
            value={searchText}
            ref={inputRef}
          />
          <Button
            type="submit"
            variant="icon"
            size="icon"
            onClick={handleSearch}
            disabled={searchText.length === 0}
          >
            <Search className="w-3 h-3" />
          </Button>
        </div>
        <DropdownMenuSeparator />
        {customZones.map((zone) => (
          <DropdownMenuCheckboxItem
            key={zone.id}
            onCheckedChange={(checked) =>
              handleToggleZoneSelect(zone.name, checked)
            }
            checked={selectedValues.has(zone.name)}
          >
            <span>{zone.name}</span>
          </DropdownMenuCheckboxItem>
        ))}
        {remainingValues.map((value) => (
          <>
            <DropdownMenuCheckboxItem
              key={value}
              onCheckedChange={() => handleRemove(value)}
              checked
            >
              <span>{value}</span>
            </DropdownMenuCheckboxItem>
          </>
        ))}
        <DropdownMenuItem onClick={handleReset} className="pl-8">
          Clear
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
